import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { SITE } from "../../data/site";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Pricing", to: "/pricing" },
  { label: "How It Works", to: "/#how" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/", { state: { scrollTo: id } });
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <header className="no-print fixed top-4 inset-x-0 z-50 flex justify-center px-4">
      <nav
        data-testid="main-navbar"
        className={`glass w-full max-w-4xl rounded-full px-4 sm:px-6 py-2.5 flex items-center justify-between transition-all duration-300 ${
          scrolled ? "shadow-[0_10px_40px_rgba(0,132,255,0.14)]" : ""
        }`}
        aria-label="Main navigation"
      >
        <button
          data-testid="nav-logo"
          onClick={() => handleNav("/")}
          className="flex items-center gap-2 font-display font-extrabold text-lg text-brand-ink"
        >
          <span>Clean<span className="text-brand-blue">MyCar</span></span>
        </button>

        <div className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => handleNav(l.to)}
              className="px-4 py-2 rounded-full text-sm font-medium text-brand-muted hover:text-brand-ink hover:bg-black/5 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-book-now"
            onClick={() => handleNav("/book")}
            className="group hidden sm:inline-flex items-center gap-1.5 bg-brand-blue text-white text-sm font-semibold pl-5 pr-3 py-2.5 rounded-full hover:scale-[1.03] active:scale-95 transition-transform"
          >
            Book Now
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden grid place-items-center w-10 h-10 rounded-full hover:bg-black/5"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="glass absolute top-20 left-4 right-4 rounded-3xl p-3 md:hidden flex flex-col">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => handleNav(l.to)}
              className="text-left px-4 py-3 rounded-2xl font-medium text-brand-ink hover:bg-black/5"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="mobile-book-now"
            onClick={() => handleNav("/book")}
            className="mt-1 bg-brand-blue text-white font-semibold px-4 py-3 rounded-2xl"
          >
            Book Now
          </button>
        </div>
      )}
    </header>
  );
}
